import type { UserSessionQuery } from './__generated__/graphql';
import type { Authenticated, AuthenticationError, AuthenticationState } from './AuthContext';
import type { UserWithHumanToken } from './User';

export type TrackError = (
  label: string,
  error?: Error,
  data?: {
    [k: string]: unknown;
  },
) => void;

type AuthenticationStateWithHumanToken =
  | Exclude<AuthenticationState, Authenticated>
  | { state: 'authenticated'; user: UserWithHumanToken };

class InvalidSessionDataError extends Error {
  constructor(
    readonly label:
      | 'invalid data returned: missing UserSession'
      | 'invalid data returned: missing Human.email',
  ) {
    super(label);
  }
}

export function authenticationRequestError(
  error: unknown,
  trackError: TrackError | undefined,
): AuthenticationError {
  let message = 'UserSession request failed';
  if (error instanceof InvalidSessionDataError) {
    message = error.label;
    trackError?.(message);
  } else {
    // Transport errors can embed session credentials in their message, cause, and payload.
    trackError?.('UserSession error', new Error(message), {
      errorName: error instanceof Error ? error.name : 'Error',
    });
  }
  return { state: 'error', user: undefined, message };
}

function parseUserSession(data: UserSessionQuery | undefined): UserWithHumanToken | null {
  const userSession = data?.web.userSession;
  if (!userSession) {
    throw new InvalidSessionDataError('invalid data returned: missing UserSession');
  }
  const human = userSession.human;
  if (!human) return null;
  if (!human.email) {
    throw new InvalidSessionDataError('invalid data returned: missing Human.email');
  }

  return {
    humanToken: human.token,
    customerUuid: human.customer_uuid || null,
    email: human.email,
    fullName: human.full_name || human.first_name || 'Customer',
    firstName: human.first_name || human.full_name || 'Customer',
    hasOpendoorEmail: human.email.endsWith('@opendoor.com'),
    hasOpendoorInternalAccess: human.opendoor_internal_access || false,
    primaryPhone: human.primary_phone || null,
    impersonated: human.impersonated || false,
  };
}

export const parseAuthentication = (
  queryResult: {
    data: UserSessionQuery | undefined;
    error?: Error | undefined;
    loading: boolean;
  },
  trackError: TrackError | undefined,
): AuthenticationStateWithHumanToken => {
  if (queryResult.loading) {
    return { state: 'authenticating', user: undefined };
  }
  if (queryResult.error) {
    return authenticationRequestError(queryResult.error, trackError);
  }
  try {
    const user = parseUserSession(queryResult.data);
    return user ? { state: 'authenticated', user } : { state: 'not_authenticated', user: null };
  } catch (error) {
    return authenticationRequestError(error, trackError);
  }
};
